import React, { useState } from "react";
import { theme } from "antd";
import { useTranslate } from "@refinedev/core";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  DotsSixVerticalIcon,
  EyeIcon,
  EyeSlashIcon,
  XIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { Card, Divider, IconButton, IconChip } from "@libertify/ui";
import {
  ADDON_CATALOG,
  UI_ONLY_ADDON_IDS,
  AddonDef,
  AddonId,
  SettingsItemDef,
  getConfigItemVisual,
} from "../data/addons";
import type { ConfigItemId } from "./config-cards/registry";
import { AddAddonModal } from "./AddAddonModal";
import { ConfirmRemovalModal } from "./ConfirmRemovalModal";
import { DashedAddButton } from "./DashedAddButton";
import {
  SectionLabel,
  Row,
  AddonRow,
  DragHandle,
  OrderNum,
  SelectButton,
  ItemName,
} from "./AddonRail.styles";

/** Prototype add-ons with no backend `AddOnType`, so their enabled state can't
 *  be persisted — their toggle renders read-only. */
const UI_ONLY_IDS: ReadonlySet<string> = new Set(UI_ONLY_ADDON_IDS);

/**
 * What the trailing button on each add-on row does. The two are mutually
 * exclusive: creating an experience removes add-ons outright, whereas editing one
 * keeps them and only flips whether they're enabled.
 */
export type AddonRailRowAction =
  | { kind: "remove"; onRemove: (id: AddonId) => void }
  | {
      kind: "visibility";
      /** Whether the add-on is currently shown to viewers of the experience. */
      isVisible: (id: AddonId) => boolean;
      /** Rows with a show/hide or generate request in flight (shows a spinner). */
      pendingIds?: readonly AddonId[];
      onVisibilityChange: (id: AddonId, visible: boolean) => void;
    };

interface SortableAddonRowProps {
  def: AddonDef;
  /** 1-based position shown in the row. */
  position: number;
  active: boolean;
  onSelect: () => void;
  /** Trailing action, rendered by the rail (remove or enable/disable). */
  action: React.ReactNode;
}

/** A single reorderable add-on row. The drag handle, the select action and the
 *  remove action are each their own real button (keyboard- and AT-friendly);
 *  the row itself is a passive container. */
const SortableAddonRow: React.FC<SortableAddonRowProps> = ({
  def,
  position,
  active,
  onSelect,
  action,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: def.id });
  const { Icon, color, weight } = getConfigItemVisual(def.id);

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <AddonRow
      ref={setNodeRef}
      style={style}
      $token={token}
      $active={active}
      $dragging={isDragging}
    >
      <DragHandle
        $token={token}
        type="button"
        aria-label={t("createExperience.customize.reorderTooltip")}
        {...attributes}
        {...listeners}
      >
        <DotsSixVerticalIcon size={16} />
      </DragHandle>
      <OrderNum $token={token}>{position}</OrderNum>
      <SelectButton type="button" onClick={onSelect}>
        <IconChip color={color} size="s" icon={<Icon weight={weight} />} />
        <ItemName>{t(def.labelKey)}</ItemName>
      </SelectButton>
      {action}
    </AddonRow>
  );
};

export interface AddonRailProps {
  /** Fixed "Settings" entries — `SETTINGS_ITEMS` (create) or
   *  `EDIT_SETTINGS_ITEMS` (edit). */
  settingsItems: SettingsItemDef[];
  /** The experience's add-ons, in display order. */
  addonIds: AddonId[];
  /** Which rail entry is currently showing its card on the right. */
  active: ConfigItemId;
  onSelect: (id: ConfigItemId) => void;
  /** Fired with every add-on picked in the modal, added together. */
  onAddAddons: (ids: AddonId[]) => void;
  onReorderAddons: (from: number, to: number) => void;
  /** What each add-on row's trailing button does — remove, or enable/disable. */
  rowAction: AddonRailRowAction;
}

/**
 * The left rail shared by the create flow's #customize view and the experience
 * edit page: a Settings section, then the experience's add-ons — numbered,
 * reorderable — and an "Add a new add-on" picker.
 *
 * Owns the add/remove modal state (both pages want identical behaviour) but not
 * the selection or the add-on list itself, which live in the page so each can
 * persist them its own way. The trailing row action differs per page and is
 * supplied via `rowAction`.
 */
export const AddonRail: React.FC<AddonRailProps> = ({
  settingsItems,
  addonIds,
  active,
  onSelect,
  onAddAddons,
  onReorderAddons,
  rowAction,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const [addModalOpen, setAddModalOpen] = useState(false);
  // `removeTarget` intentionally stays set while the confirm modal fades out
  // (only `removeOpen` toggles), so the add-on name doesn't blank mid-close.
  const [removeTarget, setRemoveTarget] = useState<AddonId | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const canAddMore = ADDON_CATALOG.some((a) => !addonIds.includes(a.id));
  const removeName = removeTarget
    ? t(ADDON_CATALOG.find((a) => a.id === removeTarget)?.labelKey ?? "")
    : "";

  const handleConfirmRemove = () => {
    if (removeTarget && rowAction.kind === "remove") {
      rowAction.onRemove(removeTarget);
    }
    setRemoveOpen(false);
  };

  const renderRowAction = (id: AddonId) => {
    if (rowAction.kind === "remove") {
      return (
        <IconButton
          variant="danger"
          size="s"
          aria-label={t("createExperience.customize.removeAddon")}
          icon={<XIcon weight="bold" />}
          onClick={() => {
            setRemoveTarget(id);
            setRemoveOpen(true);
          }}
        />
      );
    }
    const visible = rowAction.isVisible(id);
    const pending = rowAction.pendingIds?.includes(id) ?? false;
    // A UI-only prototype has no backend add-on behind it, so its visibility
    // can't be stored — it stays read-only until that add-on is wired.
    const readOnly = UI_ONLY_IDS.has(id);
    return (
      <IconButton
        variant="tertiary"
        size="s"
        loading={pending}
        disabled={pending || readOnly}
        aria-label={t(
          visible
            ? "experienceEdit.rail.hideAddon"
            : "experienceEdit.rail.showAddon"
        )}
        icon={visible ? <EyeIcon /> : <EyeSlashIcon />}
        onClick={() => rowAction.onVisibilityChange(id, !visible)}
      />
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active: dragged, over } = event;
    if (!over || dragged.id === over.id) return;
    const from = addonIds.indexOf(dragged.id as AddonId);
    const to = addonIds.indexOf(over.id as AddonId);
    if (from !== -1 && to !== -1) onReorderAddons(from, to);
  };

  return (
    <>
      <Card
        style={{ width: 300, maxWidth: "100%", flexShrink: 0 }}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            padding: "12px 12px 24px",
          },
        }}
      >
        <SectionLabel $token={token}>
          {t("createExperience.customize.settings")}
        </SectionLabel>
        {settingsItems.map((s) => {
          const { Icon, color, weight } = getConfigItemVisual(s.id);
          return (
            <Row
              key={s.id}
              $token={token}
              $active={active === s.id}
              type="button"
              onClick={() => onSelect(s.id)}
            >
              <IconChip
                color={color}
                size="s"
                icon={<Icon weight={weight} />}
              />
              <ItemName>{t(s.labelKey)}</ItemName>
            </Row>
          );
        })}

        <SectionLabel $token={token}>
          {t("createExperience.customize.addonCustomization")}
        </SectionLabel>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={addonIds}
            strategy={verticalListSortingStrategy}
          >
            {addonIds.map((id, i) => {
              const def = ADDON_CATALOG.find((a) => a.id === id);
              if (!def) return null;
              return (
                <SortableAddonRow
                  key={id}
                  def={def}
                  position={i + 1}
                  active={active === id}
                  onSelect={() => onSelect(id)}
                  action={renderRowAction(id)}
                />
              );
            })}
          </SortableContext>
        </DndContext>

        {canAddMore && (
          <>
            <Divider spacing="lg" color={token.colorBorderSecondary} />
            <DashedAddButton
              icon={<PlusIcon size={16} />}
              label={t("createExperience.customize.addAddon")}
              onClick={() => setAddModalOpen(true)}
            />
          </>
        )}
      </Card>

      <AddAddonModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        addedIds={addonIds}
        onAdd={onAddAddons}
      />
      {rowAction.kind === "remove" && (
        <ConfirmRemovalModal
          open={removeOpen}
          i18nKey="createExperience.removeAddonModal"
          name={removeName}
          onCancel={() => setRemoveOpen(false)}
          onConfirm={handleConfirmRemove}
        />
      )}
    </>
  );
};

export default AddonRail;
